import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { colors } from '../../../core';
import BaseModal from '../BaseModal';
import Button from '../Button';
import Card from '../Card';
import Heading from '../Heading';
import LoadingSpinner from '../LoadingSpinner';
import Text from '../Text';
import TextAreaInput from '../TextAreaInput';
import TextInput from '../TextInput';

export type ReportFormType = {
  title: string;
  description: string;
};

export interface ReportButtonProps {
  reportButtonText: string;
  handleOnClick: () => void;
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  handleReportSubmit: (values: ReportFormType) => void;
  loading?: boolean;
}

const ReportButtonStyle = styled(Button)`
  position: fixed;
  bottom: 20px;
  left: 20px;
`;

const ButtonsWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 2rem;
`;

const CardContainer = styled.div`
  max-width: 550px;
`;

const ReportButton = ({
  reportButtonText,
  handleOnClick,
  openModal,
  setOpenModal,
  handleReportSubmit,
  loading,
}: ReportButtonProps) => {
  const { control, handleSubmit, getValues, errors } = useForm<ReportFormType>();

  const onReportSubmit = () => {
    handleReportSubmit(getValues());
  };

  return (
    <div>
      <ReportButtonStyle
        variant="primary"
        size="xsmall"
        onClick={handleOnClick}
        backGroundColor={colors.error}
      >
        {reportButtonText}
      </ReportButtonStyle>
      {openModal ? (
        <BaseModal onClose={() => setOpenModal(false)}>
          <CardContainer>
            <Card>
              <Heading>Report an Issue</Heading>
              <Text>
                {`Please include where the issue occurred in the 'Title' (E.g. Liver > Ind Lesion > Missing
              location option) and in the 'Description', describe the issue, including what was being
              done before it happened, and any troubleshooting steps completed.`}
              </Text>

              <Controller
                control={control}
                name={'title'}
                rules={{
                  required: 'This field is required.',
                }}
                render={(props: any) => (
                  <TextInput
                    label="Title"
                    name="title"
                    type="text"
                    errorMessage={errors.title?.type === 'required' ? errors.title?.message : ''}
                    {...props}
                  />
                )}
              />

              <Controller
                control={control}
                name={'description'}
                rules={{
                  required: 'This field is required.',
                }}
                render={(props: any) => (
                  <TextAreaInput
                    label="Description"
                    name="description"
                    errorMessage={
                      errors.description?.type === 'required' ? errors.description?.message : ''
                    }
                    {...props}
                  />
                )}
              />

              <ButtonsWrapper>
                <Button
                  variant="primary"
                  size="xsmall"
                  onClick={handleSubmit(onReportSubmit)}
                  backGroundColor={colors.error}
                >
                  {loading ? <LoadingSpinner size="small" color={colors.white} /> : 'Report Issue'}
                </Button>
                <Button
                  variant="secondary"
                  size="xsmall"
                  disabled={loading}
                  onClick={() => setOpenModal(false)}
                >
                  Cancel
                </Button>
              </ButtonsWrapper>
            </Card>
          </CardContainer>
        </BaseModal>
      ) : null}
    </div>
  );
};

export default ReportButton;
